import React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  FormDataConsumer,
  useNotify,
  useRefresh,
  useRecordContext,
  Button,
} from "react-admin";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";

const targetChoices = [
  { id: "all", name: "An alle User" },
  { id: "selected", name: "An ausgewählte User" },
];

const csvFromList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value ?? "";
};

const listFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value ?? [];
};

const userIdListFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item.match(/^\d+$/) ? Number(item) : item))
      .filter((item) => typeof item === "number");
  }
  return value ?? [];
};

const SendServiceMailButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleSend = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/service-mails/${record.id}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        notify(data.detail || "Versand fehlgeschlagen", { type: "error" });
        return;
      }
      notify("Service-Mail wurde in die Queue gestellt", { type: "info" });
      refresh();
    } catch (error) {
      notify("Versand fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button
      label="Send Service Mail"
      onClick={handleSend}
      startIcon={<SendIcon />}
      disabled={record.status !== "draft"}
    />
  );
};

const PreviewServiceMailButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record) return null;

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/service-mails/${record.id}/preview`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        notify(data.detail || "Preview fehlgeschlagen", { type: "error" });
        return;
      }
      const html = await response.text();
      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        notify("Popup blockiert", { type: "warning" });
        return;
      }
      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (error) {
      notify("Preview fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button label="Preview Mail" onClick={handlePreview} startIcon={<VisibilityIcon />} />
  );
};

export const ServiceMailList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <TextField source="subject" />
      <TextField source="target_type" />
      <TextField source="status" />
      <DateField source="sent_at" showTime />
    </Datagrid>
  </List>
);

const ServiceMailFormFields = () => (
  <>
    <TextInput source="name" fullWidth />
    <TextInput source="subject" fullWidth />
    <TextInput source="from_email" fullWidth />
    <SelectInput source="target_type" choices={targetChoices} fullWidth defaultValue="all" />
    <FormDataConsumer>
      {({ formData }) =>
        formData.target_type === "selected" ? (
          <>
            <TextInput
              source="recipient_emails"
              fullWidth
              multiline
              minRows={2}
              label="Empfänger E-Mails (kommagetrennt)"
              format={csvFromList}
              parse={listFromCsv}
            />
            <TextInput
              source="recipient_user_ids"
              fullWidth
              multiline
              minRows={2}
              label="Empfänger User-IDs (kommagetrennt)"
              format={csvFromList}
              parse={userIdListFromCsv}
            />
          </>
        ) : null
      }
    </FormDataConsumer>
    <TextInput source="body_html" fullWidth multiline minRows={6} />
    <TextInput source="body_text" fullWidth multiline minRows={4} />
    <TextInput source="status" disabled />
    <TextInput source="sent_at" disabled />
  </>
);

export const ServiceMailCreate = () => (
  <Create>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <ServiceMailFormFields />
    </SimpleForm>
  </Create>
);

export const ServiceMailEdit = () => (
  <Edit>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <ServiceMailFormFields />
      <PreviewServiceMailButton />
      <SendServiceMailButton />
    </SimpleForm>
  </Edit>
);
